import DeleteIcon from '@mui/icons-material/Delete';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import MovieIcon from '@mui/icons-material/Movie';
import WorkIcon from '@mui/icons-material/Work';
import { alpha, Box, Grid, MenuItem, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { FadingBalls } from 'react-cssfx-loading';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Image } from '@/components/ImageAvatar/ImageAvatar';
import { styles } from '@/components/StyledComponents';
import { Popover } from '@/components/ui/popover';
import { Text } from '@/components/ui/text';
import { Tooltip } from '@/components/ui/tooltip';
import { paths, STORAGE } from '@/constants';
import { useCompaniesList } from '@/data/entities/companies';
import { assetCategory } from '@/enums';
import { useColorMode, useCompanyId } from '@/hooks';
import { useWorkspace } from '@/hooks/useWorkspace';
import { modals } from '@/modals';
import { usePrefValue } from '@/preferences';
import { CompanyHeaderText, MainContainer, NameCompanyText, TwoArrowGrid } from './NavigationCompany.styles';

interface props {
    setOpen?: (state: string) => void;
}

export const NavigationCompany = ({ setOpen }: props) => {
    const [companiesMenuAnchor, setCompaniesMenuAnchor] = useState<HTMLElement | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isHover, setHover] = useState(0);

    const handleDeleteProject = () => {
        modals.confirm({
            message: t('project.delete-project'),
            cancelLabel: t('buttons.no-delete'),
            onConfirm: () => {},
        });
    };
    const theme = useTheme();
    const isDarkMode = useColorMode();

    const companyId = useCompanyId();
    const side_bar = usePrefValue('side_bar');

    const { data: companies, isFetching } = useCompaniesList({});
    const switchWorkspace = useWorkspace();
    const company = companies?.find(el => el.id === companyId);

    useEffect(() => {
        if (!companyId && companies?.length) switchWorkspace({ companyID: companies[0]?.id ?? 0 });
    }, [companyId, companies, switchWorkspace]);

    const CompanyActions = () => (
        <Grid container flexDirection="column" borderRadius="8px" sx={{ background: theme => theme.palette.secondary.light }}>
            <Grid container sx={[styles.iconTextRowMenu]} onClick={() => navigate(paths.company.index)}>
                <WorkIcon
                    sx={{
                        color: theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22),
                        fontSize: '1.25rem',
                    }}
                />
                <Text variant="body2" color="text-text-primary" children={company?.name || t('company.go-company')} />
            </Grid>
            <Grid
                container
                sx={[styles.iconTextRowMenu]}
                onClick={e => {
                    e.stopPropagation();
                    navigate(paths.profile.index);
                }}
            >
                <ImportExportIcon
                    sx={{
                        transform: 'rotate(90deg)',
                        color: theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22),
                        fontSize: '1.25rem',
                    }}
                />
                <Text variant="body2" color="text-text-primary" children={t('company.change-company')} />
            </Grid>
            <Grid container sx={[styles.iconTextRowMenu]} onClick={handleDeleteProject}>
                <DeleteIcon
                    sx={{
                        color: theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22),
                        fontSize: '1.25rem',
                    }}
                />
                <Text variant="body2" color="text-text-primary" children={t('company.delete-company')} />
            </Grid>
        </Grid>
    );

    return (
        <div className="flex w-full gap-[4px] flex-nowrap items-center">
            <MainContainer
                container
                onMouseEnter={() => setHover(1)}
                onMouseLeave={() => setHover(0)}
                onClick={() => {
                    if (location.pathname !== paths.company.index) navigate(paths.company.index);
                    setOpen?.('company');
                }}
                sx={[
                    side_bar &&
                        (location.pathname === paths.company.index || isHover === 1) && {
                            background: theme => alpha(theme.palette.secondary.light, 0.04),
                        },
                    !isDarkMode &&
                        location.pathname === paths.company.index &&
                        side_bar && {
                            background: theme => alpha(theme.palette.secondary.main, 0.1),
                        },
                    !isDarkMode &&
                        isHover === 1 &&
                        side_bar && {
                            background: theme => theme.custom.c7,
                        },
                ]}
            >
                <Tooltip
                    title={<CompanyActions />}
                    placement="right-start"
                    sideOffset={4}
                    contentClassName="p-0 rounded-lg overflow-hidden bg-bg-paper"
                >
                    <div className="flex">
                        <Image
                            cursor="pointer"
                            width="32px"
                            height="32px"
                            margin="0"
                            backgroundColor={
                                isHover !== 0 || location.pathname === paths.company.index ? theme.palette.text.secondary : theme.custom.c5
                            }
                            sx={[
                                {
                                    opacity: location.pathname === paths.company.index || isHover !== 0 ? 1 : 0.6,
                                },
                            ]}
                            border={theme => (isHover === 20 ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent')}
                            src={
                                company?.assets?.some(item => item.category === assetCategory.image)
                                    ? STORAGE + (company.assets.find(item => item.category === assetCategory.image)?.path ?? '')
                                    : ''
                            }
                            alt={company ? company.name : ' '}
                            title={company ? company.name : 'company'}
                            children={
                                <MovieIcon
                                    sx={{
                                        color: theme => (isDarkMode ? 'inherit' : theme.custom.c22),
                                        fontSize: '1.25rem',
                                    }}
                                />
                            }
                        />
                    </div>
                </Tooltip>
                {side_bar && (
                    <div className="gap-[8px]">
                        <CompanyHeaderText children={t('company.company')} />
                        <NameCompanyText
                            children={company ? company.name : ' '}
                            variant="body2"
                            sx={[
                                isHover === 1 && {
                                    color: theme => (isDarkMode ? theme.palette.secondary.main : theme.custom.c22),
                                },
                                isHover !== 1 && !isDarkMode && { color: theme => theme.custom.c22 },
                                location.pathname === paths.company.index && {
                                    color: theme => theme.palette.secondary.main,
                                },
                            ]}
                        />
                    </div>
                )}
            </MainContainer>

            {side_bar && (
                <>
                    <TwoArrowGrid
                        container
                        sx={[
                            !!companiesMenuAnchor && {
                                background: theme => (isDarkMode ? alpha(theme.palette.secondary.light, 0.04) : theme.custom.c7),
                            },
                        ]}
                        onClick={event => setCompaniesMenuAnchor(event.currentTarget)}
                    >
                        <ImportExportIcon
                            sx={{
                                transform: 'rotate(90deg)',
                                '&:hover': { cursor: 'pointer' },
                            }}
                            color={companiesMenuAnchor ? 'secondary' : 'info'}
                        />
                    </TwoArrowGrid>
                    <Popover
                        anchorEl={companiesMenuAnchor}
                        open={!!companiesMenuAnchor}
                        onClose={() => setCompaniesMenuAnchor(null)}
                        side="bottom"
                        align="start"
                        className="shadow-none rounded-lg flex flex-col overflow-hidden bg-bg-paper"
                    >
                        <Box
                            sx={{
                                gap: '8px',
                                maxWidth: '270px',
                                borderRadius: '8px',
                                minHeight: '0',
                                overflowY: 'auto',
                            }}
                        >
                            {isFetching ? (
                                <FadingBalls color="text.primary" width="40px" height="10px" duration="0.3s" />
                            ) : (
                                <Grid
                                    container
                                    sx={{
                                        background: theme => (isDarkMode ? theme.palette.secondary.light : theme.custom.c7),
                                        padding: '16px',
                                        overflowY: 'auto',
                                    }}
                                >
                                    {companies?.length ? (
                                        <div>
                                            {companies
                                                .filter(company => company.is_personal)
                                                .map(item => (
                                                    <MenuItem
                                                        key={item.id}
                                                        value={item.name}
                                                        sx={[styles.textOneLine]}
                                                        onClick={() => {
                                                            setCompaniesMenuAnchor(null);
                                                            navigate(paths.company.index);
                                                            switchWorkspace({ companyID: item.id });
                                                            setHover(0);
                                                        }}
                                                    >
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                            <Typography
                                                children={t('company.companies')}
                                                mt="8px"
                                                p="8px"
                                                color={theme => theme.palette.info.contrastText}
                                            />
                                            {companies
                                                .filter(company => !company.is_personal)
                                                .map(item => (
                                                    <MenuItem
                                                        key={item.id}
                                                        value={item.name}
                                                        sx={[styles.textOneLine]}
                                                        onClick={() => {
                                                            setCompaniesMenuAnchor(null);
                                                            navigate(paths.company.index);
                                                            switchWorkspace({ companyID: item.id });
                                                            setHover(0);
                                                        }}
                                                    >
                                                        {item.name}
                                                    </MenuItem>
                                                ))}
                                        </div>
                                    ) : (
                                        <Text variant="body1" children={t('company.no-companies')} />
                                    )}
                                </Grid>
                            )}
                        </Box>
                    </Popover>
                </>
            )}
        </div>
    );
};
